import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Button from '../components/UI/Button';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge from '../components/UI/StatusBadge';
import { useTableSort } from '../components/UI/tableSort';
import { apiUrl } from '../config/api';
import KpiCard from '../components/Admin/KpiCard';
import { ratesMetrics } from '../utils/adminMetrics';
import { exportToCsv } from '../utils/adminExport';
import { AuthContext } from '../context/AuthContext';
import { auditModalView } from '../utils/auditClient';
import Pager from '../components/UI/Pager';

const PAGE_SIZE = 10;

const ManageInterestRates = () => {
  const { user } = useContext(AuthContext);
  const canManageConfig = Array.isArray(user?.permissions) && user.permissions.includes('ManageConfig');
  const [rates, setRates] = useState([]);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRate, setSelectedRate] = useState(null);
  const [formData, setFormData] = useState({ rate: '', description: '', is_active: true });
  const [message, setMessage] = useState('');
  const [formErrors, setFormErrors] = useState({ rate: '' });
  const [confirmAction, setConfirmAction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      const token = localStorage.getItem('token');
  const response = await axios.get(apiUrl('/api/interestrates'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ratesWithDropdownFlag = response.data.map(rate => ({ ...rate, showActions: false }));
      setRates(ratesWithDropdownFlag);
      setError(null);
    } catch (err) {
      setError(`Error fetching rates: ${err.response?.status === 404 ? 'Interest rates endpoint not found' : err.message}`);
    }
  };

  const validateForm = () => {
    let valid = true;
    const errors = { rate: '' };
    if (!formData.rate || isNaN(formData.rate)) {
      errors.rate = 'Interest rate is required and must be a number';
      valid = false;
    }
    setFormErrors(errors);
    return valid;
  };

  const handleAddRate = async () => {
    if (!validateForm()) return;
    try {
      const token = localStorage.getItem('token');
  await axios.post(apiUrl('/api/interestrates'), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      setFormData({ rate: '', description: '', is_active: true });
      setFormErrors({ rate: '' });
      setMessage('Interest rate added successfully');
      fetchRates();
    } catch (err) {
      const errorResponse = err.response?.data;
      let errorMsg = 'Registration failed';
      if (errorResponse?.error?.includes('duplicate key value violates unique constraint')) {
        errorMsg = 'Interest rate already exists';
      } else if (errorResponse?.error) {
        errorMsg = errorResponse.error;
      } else if (err.response?.status === 404) {
        errorMsg = 'Interest rates endpoint not found';
      }
      setMessage(errorMsg);
    }
  };

  const handleUpdateRate = async () => {
    if (!validateForm()) return;
    try {
      const token = localStorage.getItem('token');
  await axios.put(apiUrl(`/api/interestrates/${selectedRate.id}`), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      setFormData({ rate: '', description: '', is_active: true });
      setFormErrors({ rate: '' });
      setMessage('Interest rate updated successfully');
      fetchRates();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error updating interest rate');
    }
  };

  // Disable/Enable interest rate
  const handleToggleRate = async (rateId, action) => {
    try {
      const token = localStorage.getItem('token');
  const endpoint = action === 'disable' ? `/api/interestrates/${rateId}/disable` : `/api/interestrates/${rateId}/enable`;
  await axios.put(apiUrl(endpoint), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`${action.charAt(0).toUpperCase() + action.slice(1)} successful`);
      setConfirmAction(null);
      fetchRates();
    } catch (err) {
      setMessage(err.response?.data?.error || `Error ${action} interest rate`);
    }
  };

  const requestRateToggle = (rateId, action, rateValue) => {
    setConfirmAction({ rateId, action, rateValue });
  };

  const openModal = (rate = null) => {
    setSelectedRate(rate);
    setFormData(rate ? { ...rate } : { rate: '', description: '', is_active: true });
    setMessage('');
    setFormErrors({ rate: '' });
    setShowModal(true);
    auditModalView(rate ? 'Edit Interest Rate' : 'Add Interest Rate');
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    const handleClickOutside = () => {
      setRates(prev => prev.map(rate => ({ ...rate, showActions: false })));
    };
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page on search
  }, [searchTerm]);

  const getMessageClass = () => {
    if (message.includes('Error') || message === 'Interest rate already exists' || message === 'Registration failed') {
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    } else if (message.includes('successful')) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    }
    return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
  };

  // Search filter
  const filteredRates = rates.filter(rate =>
    String(rate.rate).toLowerCase().includes(searchTerm.toLowerCase()) ||
    (rate.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (rate.is_active ? 'active' : 'inactive').includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredRates.length / PAGE_SIZE);
  // A click on a column sorts every rate, then the page is taken.
  const rateSort = useTableSort(filteredRates);
  const paginatedRates = rateSort.rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="max-w-full min-h-screen">

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-3 mb-2">
        {(() => { const m = ratesMetrics(rates); const sorted=(rates||[]).map(r=>Number(r.rate)).filter(n=>!isNaN(n)).sort((a,b)=>a-b); const mid=sorted.length? (sorted.length%2?sorted[(sorted.length-1)/2]:(sorted[sorted.length/2-1]+sorted[sorted.length/2])/2):0; return (
          <>
            <KpiCard label="Total Rates" value={m.total} color="gray" />
            <KpiCard label="Active Rates" value={m.active} color="green" />
            <KpiCard label="Inactive Rates" value={m.inactive} color={m.inactive>0?'amber':'green'} />
            <KpiCard label="Median Rate (%)" value={mid} color="blue" />
          </>
        ); })()}
      </div>

      {error && <div className="p-2 rounded-lg mb-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>}
      {message && <div className={`p-2 rounded-lg mb-2 flex items-center justify-between ${getMessageClass()}`}>
        <span>{message}</span>
        <Button
          onClick={() => setMessage('')}
          variant="text"
          size="sm"
          className="ml-4 rounded-full"
          aria-label="Close"
          title="Close"
        >
          &times;
        </Button>
      </div>}

      {confirmAction && (
        <div className="flex items-center justify-between bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 p-2 rounded mb-4 shadow border border-yellow-200 dark:border-yellow-700/50">
          <span>
            Are you sure you want to <strong>{confirmAction.action}</strong> the interest rate{' '}
            <strong>{confirmAction.rateValue}%</strong>?
          </span>
          <div className="flex gap-2">
            <Button
              onClick={() => handleToggleRate(confirmAction.rateId, confirmAction.action)}
              variant={confirmAction.action === 'enable' ? 'success' : 'danger'}
              size="sm"
              busyLabel="Working…"
            >
              Confirm
            </Button>
            <Button
              onClick={() => setConfirmAction(null)}
              variant="secondary"
              size="sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!showModal && (
      <>
      <div className="mb-2 flex flex-col sm:flex-row justify-between items-center gap-2">
        {/* Search on the left */}
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search interest rates..."
          className="p-2 w-full sm:max-w-xs"
        />
        {/* Add button, export and pagination side by side on the right */}
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          {canManageConfig && (
            <Button 
              onClick={() => openModal()} 
              variant="primary"
              size="md"
            >
              Add Interest Rate
            </Button>
          )}
          {canManageConfig && (
            <Button
              onClick={() => exportToCsv('interest_rates.csv', rates.map(r=>({ID:r.id,Rate:r.rate,Description:r.description||'',Active:r.is_active?'Yes':'No'})))}
              variant="secondary"
              size="md"
            >
              Export CSV
            </Button>
          )}
          <Pager page={currentPage} pages={totalPages} onPage={setCurrentPage} />
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900">
        <ResponsiveTable
          data={paginatedRates}
          sort={rateSort.sort}
          onSortChange={rateSort.onSortChange}
          emptyMessage="No results found."
          columns={[
            { field: 'rate', header: 'Rate (%)', formatter: (v) => <span className="text-gray-700 dark:text-slate-200">{v}</span> },
            { field: 'description', header: 'Description', formatter: (v) => <span className="text-gray-700 dark:text-slate-200">{v || 'N/A'}</span> },
            {
              field: 'is_active',
              header: 'Status',
              formatter: (v) => (
                <StatusBadge status={v ? 'Active' : 'Inactive'} />
              ),
            },
          ]}
          actions={[
            {
              label: 'Edit',
              condition: () => canManageConfig,
              onClick: (rate) => openModal(rate),
              className: 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 transition-colors',
            },
            {
              label: 'Disable',
              condition: (rate) => canManageConfig && rate.is_active,
              onClick: (rate) => requestRateToggle(rate.id, 'disable', rate.rate),
              className: 'bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300 transition-colors',
            },
            {
              label: 'Enable',
              condition: (rate) => canManageConfig && !rate.is_active,
              onClick: (rate) => requestRateToggle(rate.id, 'enable', rate.rate),
              className: 'bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 transition-colors',
            },
          ]}
        />
      </div>
      </>
      )}

      {showModal && (
        <FullScreenPanel
          isOpen
          onClose={() => setShowModal(false)}
          title={selectedRate ? 'Edit Interest Rate' : 'Add Interest Rate'}
        >
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                selectedRate ? handleUpdateRate() : handleAddRate();
              }}
            >
              <div>
                <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Interest Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  className={`w-full ${String(formData.rate ?? '').trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                  required
                />
                {formErrors.rate && <p className="text-red-600 text-xs mt-1 font-medium">{formErrors.rate}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-indigo-700 mb-1.5 uppercase tracking-wide">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded border-gray-300 dark:border-slate-600 text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-slate-300">Active</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50 dark:border-slate-700">
                <Button type="button" onClick={() => setShowModal(false)} variant="secondary">Cancel</Button>
                <Button
                  type="submit"
                  variant="primary"
                  busyLabel="Saving…"
                  onClick={(e) => {
                    e.preventDefault();
                    const formEl = e.currentTarget.form;
                    if (formEl && !formEl.reportValidity()) return;
                    return selectedRate ? handleUpdateRate() : handleAddRate();
                  }}
                >
                  {selectedRate ? 'Update' : 'Add'} Interest Rate
                </Button>
              </div>
            </form>
        </FullScreenPanel>
      )}
    </div>
  );
};

export default ManageInterestRates;